import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';
import { Signals, useSignaling } from '@/contexts/SignalingContext';
import { Crown, Users } from 'lucide-react';

type GenderPreference = 'male' | 'female';

interface GenderFilterState {
  genderPreference: GenderPreference;
  isEnabled: boolean;
}

const initialState: GenderFilterState = {
  genderPreference: 'male',
  isEnabled: false
};

export function GenderFilterDropdown() {
  const [ genderFilter, setGenderFilter ] = useState<GenderFilterState>(initialState);
  const { user } = useAuth();
  const { sendSignal, isConnected } = useSignaling();
  const [ , navigate ] = useLocation();

  const userPlan = user?.maybeSubscriptionInfo?.plan?.toLowerCase() || 'free';

  const toggleGenderFilter = () => {
    const newState = { ...genderFilter, isEnabled: !genderFilter.isEnabled };
    setGenderFilter(newState);

    // Send filter update to backend immediately
    if (isConnected) {
      sendSignal({
        signal: Signals.UpdateGenderFilter,
        genderFilter: {
          enabled: newState.isEnabled,
          preference: newState.genderPreference
        }
      });
    }
  };

  const setGenderPreference = (preference: GenderPreference) => {
    const newState = { ...genderFilter, genderPreference: preference };
    setGenderFilter(newState);

    // Send filter update to backend immediately
    if (isConnected) {
      sendSignal({
        signal: Signals.UpdateGenderFilter,
        genderFilter: {
          enabled: newState.isEnabled,
          preference: newState.genderPreference
        }
      });
    }
  };

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-transparent hover:bg-[#273449] text-white border border-slate-700 p-2"
            >
              {!genderFilter.isEnabled ? (
                <span className="text-sm">👤</span>
              ) : (
                <span className="text-sm">
                  {genderFilter.genderPreference === 'male' && '♂️'}
                  {genderFilter.genderPreference === 'female' && '♀️'}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {!genderFilter.isEnabled
            ? 'Gender Filter: Disabled'
            : `Gender Filter: ${genderFilter.genderPreference.charAt(0).toUpperCase() + genderFilter.genderPreference.slice(1)} only`
          }
        </TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end" className="bg-[#1C2431] border-slate-700 w-72 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-purple-400"/>
          <span className="text-white font-semibold text-sm">Gender Preferences</span>
        </div>
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between mb-3 p-2 bg-[#0E1525] rounded">
          <span className="text-white text-sm">Enable Gender Filtering</span>
          <Switch
            checked={genderFilter.isEnabled}
            onCheckedChange={toggleGenderFilter}
            disabled={userPlan === 'free'}
          />
        </div>
        {userPlan === 'free' && (
          <div className="text-center p-3 bg-purple-900/20 border border-purple-500/30 rounded mb-3">
            <Crown className="w-4 h-4 text-yellow-400 mx-auto mb-1"/>
            <p className="text-slate-300 text-xs mb-2">Premium Feature</p>
            <Button
              onClick={() => navigate('/plans')}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Upgrade
            </Button>
          </div>
        )}
        {genderFilter.isEnabled && userPlan !== 'free' && (
          <>
            {/* Gender Selection */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'male' as const, label: 'Male', icon: '♂️' },
                { value: 'female' as const, label: 'Female', icon: '♀️' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setGenderPreference(option.value)}
                  className={`p-2 rounded text-xs font-medium transition-colors border ${
                    genderFilter.genderPreference === option.value
                      ? 'bg-purple-600 border-purple-500 text-white'
                      : 'bg-[#0E1525] border-slate-600 text-slate-300 hover:bg-[#1C2431] hover:text-white'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <span>{option.icon}</span>
                    <span>{option.label}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-2 p-2 bg-[#0E1525] rounded">
              <p className="text-slate-400 text-xs">
                {`Matching only with ${genderFilter.genderPreference} users`}
              </p>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
